import React, { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import OverviewForm from "../../WorkOrder/Forms/OverviewForm";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { useMutation } from "@tanstack/react-query";
import { useSectionChanges } from "../../../hooks/useSectionChanges";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import useAuth from "../../../hooks/useAuth";
import { UserType } from "../../../utils/enum";
import { useResetValidationOnClose } from "../../../hooks/useResetValidationOnClose";

const VALIDATED_FIELDS = ["title", "clientId", "projectId", "teamId", "secondaryTeamId"];

const EditOverviewModal = ({ open, close, workOrderData }) => {
  const dispatch = useDispatch();
  const overviewRef = useRef();
  const { authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const { updateWorkOrder } = useUpdateWorkOrder();

  const overview = useSelector((state) => {
    return state.workOrder.summary.overview;
  });
  const { hasChanges, onHydrate } = useSectionChanges(overview);
  useResetValidationOnClose(VALIDATED_FIELDS);

  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // isValid() returns { valid, errors } — the bare object is always truthy, so
    // testing it directly let every incomplete form through.
    const { valid, errors } = normalizeValidationResult(overviewRef.current?.isValid?.());

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }
    try {
      await updateWorkOrderApiMutation({ id: workOrderData.id, ...overview });
      dispatch(
        showToast({
          message: "Overview updated successfully!",
          type: "success",
        }),
      );
      updateWorkOrder();
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: "Failed to update Overview.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="sm:-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Overview</h6>

        <div className="mb-2 mt-3">
          <OverviewForm
            ref={overviewRef}
            isEditMode={true}
            workOrderData={workOrderData}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          disabled={
            !hasChanges ||
            !overview?.title?.trim() ||
            !overview?.clientId ||
            (!isCustomer &&
              (!overview?.projectId || !overview?.teamId || !overview?.secondaryTeamId))
          }
          onClick={handleSubmit}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditOverviewModal;
